/** Tipos de GET /dashboard/rh e /dashboard/headcount. Valores em CENTAVOS. */

export interface SaudeBloco {
  titular: number;
  dependentes: number;
  franquiaEmpresa: number;
  repassado: number;
}

export interface Bloco {
  headcount: { total: number; pj: number; clt: number };
  salario: number;
  complemento: number;
  variaveis: number;
  encargos: number;
  retencoes: number;
  beneficios: number;
  bonificacao: number;
  custo: number;
  custoMedio: number;
  custoPj: number;
  custoClt: number;
  saude: SaudeBloco;
  emprestimos: number;
  liquido: number;
}

export interface Grupo extends Bloco {
  chave: string;
  nome: string;
  participacao: number;
  /** Custo do mesmo grupo no mês anterior (0 se não existia). */
  custoAnterior: number;
}

export interface Composicao {
  competencia: string;
  folhaPj: number;
  folhaClt: number;
  beneficios: number;
  bonificacao: number;
  custo: number;
  headcount: number;
}

export interface ResumoEmissao {
  total: number;
  emitiram: number;
  pendentes: number;
}

export interface DashboardRH {
  competencia: string;
  competencias: string[];
  status: {
    folha: { id: string; status: string } | null;
    bonificacao: { id: string; status: string; parcela: number; numeroParcelas: number } | null;
    folhasBeneficio: number;
  };
  total: Bloco;
  porVinculo: { pj: Bloco; clt: Bloco };
  anterior: Bloco;
  porTomador: (Grupo & { departamentos: Grupo[] })[];
  porDepartamento: Grupo[];
  evolucao: Composicao[];
  operacional: {
    nfsAguardandoRh: number;
    emissaoSalario: ResumoEmissao | null;
    emissaoBonificacao: ResumoEmissao | null;
    holeritesPendentes: number;
    pendenciasImportacao: number;
    saldoEmprestimos: number;
    emprestimosAtivos: number;
    cadastrosIncompletos: number;
  };
}

export interface ResumoGrupoHeadcount {
  chave: string;
  nome: string;
  total: number;
  pj: number;
  clt: number;
  salarioMedio: number;
  salarioMediano: number;
  tempoMedioMeses: number | null;
  participacao: number;
}

export interface Headcount {
  hoje: string;
  resumo: {
    total: number;
    pj: number;
    clt: number;
    emExperiencia: number;
    diasExperiencia: number;
    admissoesMes: number;
    desligamentosMes: number;
    turnover12m: number | null;
    salarioMedio: number;
    salarioMediano: number;
    comPendencia: number;
  };
  porTomador: ResumoGrupoHeadcount[];
  porDepartamento: ResumoGrupoHeadcount[];
  tempoDeCasa: { faixa: string; pj: number; clt: number }[];
  faixasSalariais: { faixa: string; pj: number; clt: number }[];
  movimentacao: { competencia: string; admissoes: number; desligamentos: number; naFolha: number | null }[];
  aniversarios: { id: string; nome: string; vinculo: "pj" | "clt"; admissao: string; anos: number }[];
  pendencias: { id: string; nome: string; vinculo: "pj" | "clt"; pendencias: string[] }[];
}

/** "2026-09" -> "set/26", para eixo de gráfico. */
export const mesCurto = (competencia: string) => {
  const [a, m] = competencia.split("-");
  const nomes = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${nomes[Number(m) - 1]}/${a.slice(2)}`;
};

/** Valor compacto para eixo e blocos: R$ 1,2 mi / R$ 350 mil / R$ 980. */
export const reaisCompacto = (centavos: number) => {
  const r = centavos / 100;
  const abs = Math.abs(r);
  const fmt = (v: number, casas: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: casas });
  if (abs >= 1_000_000) return `R$ ${fmt(r / 1_000_000, 1)} mi`;
  if (abs >= 1_000) return `R$ ${fmt(r / 1_000, abs >= 100_000 ? 0 : 1)} mil`;
  return `R$ ${fmt(r, 0)}`;
};

export const porcento = (v: number, casas = 1) =>
  `${(v * 100).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
